// Availability calculation utilities.

export const DEFAULT_START_MIN = 8 * 60;
export const DEFAULT_END_MIN = 20 * 60;
export const SLOT_STEP_MIN = 30;
export const SLOT_MIN_BOUND = 0;
export const SLOT_MAX_BOUND = 23 * 60 + 30;
export const EXTEND_STEP_MIN = 2 * 60;

export function buildTimeSlots(startMin, endMin, stepMin = SLOT_STEP_MIN) {
  const slots = [];
  const start = Math.max(SLOT_MIN_BOUND, Math.min(startMin, endMin));
  const end = Math.min(SLOT_MAX_BOUND, Math.max(startMin, endMin));
  const step = stepMin > 0 ? stepMin : SLOT_STEP_MIN;
  for (let m = start; m <= end; m += step) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
  }
  return slots;
}

export const TIME_SLOTS = buildTimeSlots(DEFAULT_START_MIN, DEFAULT_END_MIN);

export const slotId = (dayISO, time) => `${dayISO}|${time}`;

export function parseSlotId(s) {
  const [day, time] = s.split("|");
  return { day, time };
}

export function toISODate(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatTimeLabel(time) {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

const SMART_KEYWORD_RANGES = [
  { keyword: "breakfast", start: "07:00", end: "10:00" },
  { keyword: "brunch", start: "10:00", end: "14:00" },
  { keyword: "dinner", start: "17:00", end: "22:00" },
  { keyword: "coffee", start: "07:00", end: "11:00" },
  { keyword: "lunch", start: "11:00", end: "14:00" },
  { keyword: "movie night", start: "18:00", end: "22:00" },
  { keyword: "game night", start: "18:00", end: "22:00" },
  { keyword: "story time", start: "18:00", end: "20:00" },
  { keyword: "playdate", start: "09:00", end: "17:00" },
  { keyword: "birthday", start: "14:00", end: "21:00" },
  { keyword: "picnic", start: "11:00", end: "15:00" },
  { keyword: "potluck", start: "17:00", end: "21:00" },
  { keyword: "bake", start: "10:00", end: "16:00" },
  { keyword: "tea", start: "14:00", end: "17:00" },
].sort((a, b) => b.keyword.length - a.keyword.length);

const OVERRIDE_MODIFIER_RANGES = [
  { keyword: "late night", start: "21:00", end: "02:00" },
  { keyword: "afternoon", start: "12:00", end: "17:00" },
  { keyword: "morning", start: "07:00", end: "12:00" },
  { keyword: "evening", start: "17:00", end: "21:00" },
].sort((a, b) => b.keyword.length - a.keyword.length);

const SHIFT_MODIFIERS = [
  { keyword: "early", hours: -2 },
  { keyword: "late", hours: 2 },
].sort((a, b) => b.keyword.length - a.keyword.length);

const FALLBACK_RANGE = { start: "08:00", end: "20:00" };
const LATEST_SHIFTED_END = 24 * 60;

function timeToMinutes(time) {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(totalMinutes) {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function rangeToMinutes(range) {
  const start = timeToMinutes(range.start);
  let end = timeToMinutes(range.end);
  if (end <= start) end += 24 * 60;
  return { start, end };
}

function shiftRange(range, hours) {
  const duration = range.end - range.start;
  let start = range.start + hours * 60;
  let end = range.end + hours * 60;

  if (start < 0) {
    start = 0;
    end = Math.min(start + duration, LATEST_SHIFTED_END);
  }
  if (end > LATEST_SHIFTED_END) {
    end = LATEST_SHIFTED_END;
    start = Math.max(0, end - duration);
  }

  return { start, end };
}

function formatCaptionTime(time) {
  const minutes = timeToMinutes(time);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${String(mins).padStart(2, "0")} ${ampm}`;
}

function findMatch(table, lowerTitle) {
  return table.find(({ keyword }) => lowerTitle.includes(keyword));
}

export function inferRange(title) {
  const lowerTitle = title.toLowerCase();
  const keywordMatch = findMatch(SMART_KEYWORD_RANGES, lowerTitle);
  const overrideMatch = findMatch(OVERRIDE_MODIFIER_RANGES, lowerTitle);
  const shiftMatch =
    lowerTitle.includes("late night")
      ? SHIFT_MODIFIERS.find(({ keyword }) => keyword !== "late" && lowerTitle.includes(keyword))
      : findMatch(SHIFT_MODIFIERS, lowerTitle);

  const baseRange = overrideMatch || keywordMatch || FALLBACK_RANGE;
  let range = rangeToMinutes(baseRange);

  if (shiftMatch) {
    range = shiftRange(range, shiftMatch.hours);
  }

  const start = minutesToTime(range.start);
  const end = minutesToTime(range.end);
  const captionParts = [`Showing ${formatCaptionTime(start)} – ${formatCaptionTime(end)}`];
  const matchedLabel = overrideMatch?.keyword || keywordMatch?.keyword;

  if (matchedLabel) {
    captionParts.push(`matched '${matchedLabel}'`);
  }
  if (shiftMatch) {
    captionParts.push(`shifted ${shiftMatch.keyword}`);
  }

  return {
    start,
    end,
    caption:
      captionParts.length === 1
        ? captionParts[0]
        : `${captionParts[0]} · ${captionParts.slice(1).join(", ")}`,
  };
}

export function formatDayLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function formatDayShort(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Ranking rules:
 *  1. Higher participant count first.
 *  2. Within equal counts, sender-preferred slots rank first.
 *  3. Tie-break by earliest date/time.
 * Preferred slots never beat a slot with strictly higher participant count,
 * so they can't override impossible availability.
 */
export function computeRanking(meeting) {
  if (!meeting) return { ranked: [], maxCount: 0, responseCount: 0 };
  const { days = [], preferredSlots = [], responses = [] } = meeting;
  const preferredSet = new Set(preferredSlots);

  const slots = [];
  for (const day of [...days].sort()) {
    for (const t of TIME_SLOTS) {
      slots.push({ id: slotId(day, t), day, time: t });
    }
  }

  const countMap = new Map();
  const namesMap = new Map();
  for (const r of responses) {
    for (const s of r.slots || []) {
      countMap.set(s, (countMap.get(s) || 0) + 1);
      if (!namesMap.has(s)) namesMap.set(s, []);
      namesMap.get(s).push(r.name);
    }
  }

  const enriched = slots.map((s) => ({
    ...s,
    count: countMap.get(s.id) || 0,
    preferred: preferredSet.has(s.id),
    names: namesMap.get(s.id) || [],
  }));

  enriched.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.preferred !== b.preferred) return a.preferred ? -1 : 1;
    if (a.day !== b.day) return a.day < b.day ? -1 : 1;
    return a.time < b.time ? -1 : 1;
  });

  const maxCount = enriched.length ? enriched[0].count : 0;
  return { ranked: enriched, maxCount, responseCount: responses.length };
}
